import { Fragment } from 'react';

import { Flex, Text } from '@backstage/ui';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { getTypeOrNull, useSurfaceQuery } from '@spotify/confidence-graphql';
import { ActivityItem } from '@spotify/plugin-confidence-activity-react';

import { DeltaText } from '../DeltaText';
import { Expression } from '../Expression';
import { ExpressionGroup } from '../ExpressionGroup';
import type { Rule } from './types';

const extractSurfaceName = (tag: string) => {
  return tag.split('/').slice(0, 2).join('/');
};

export const tagIsExclusivityGroup = (tag: string) => {
  const [surface, _, exclusivityGroups] = tag.split('/');
  return (
    !!surface &&
    !!exclusivityGroups &&
    exclusivityGroups === 'exclusivityGroups' &&
    surface === 'surfaces'
  );
};

const ExclusivityGroups = ({
  oldTags,
  newTags,
}: {
  oldTags?: string[];
  newTags?: string[];
}) => {
  const tags = [...new Set([...(oldTags ?? []), ...(newTags ?? [])])];

  const addedTags = tags.filter(
    tag => oldTags !== undefined && !oldTags.includes(tag),
  );
  const removedTags = tags.filter(
    tag => newTags !== undefined && !newTags.includes(tag),
  );

  const name = extractSurfaceName(tags[0]);

  // XXX:
  // Because of cycling dependencies, we cannot use the useSurface hook directly.
  // but we have to use the useSurfaceQuery hook to get the surface name.
  const { data } = useSurfaceQuery({
    variables: {
      name,
    },
  });

  const surface = getTypeOrNull(data?.surface);

  const renderItem = (tag: string) => {
    const text = extractLastNameComponent(tag)!;

    if (addedTags.includes(tag)) {
      return <DeltaText oldText="" newText={text} />;
    }

    if (removedTags.includes(tag)) {
      return <DeltaText oldText={text} newText="" />;
    }

    return <Text variant="body-medium">{text}</Text>;
  };

  return (
    <ExpressionGroup>
      <Expression.Root>
        {tags.map((tag, i, arr) => (
          <Fragment key={tag}>
            {renderItem(tag)}
            {i < arr.length - 1 && (
              <Expression.Operator>, </Expression.Operator>
            )}
          </Fragment>
        ))}

        <Expression.Operator>in surface</Expression.Operator>
        <Expression.Value>{surface?.displayName}</Expression.Value>
      </Expression.Root>
    </ExpressionGroup>
  );
};

export const ExcludingSection = ({
  oldRule,
  newRule,
}: {
  oldRule?: Rule;
  newRule?: Rule;
}) => {
  const oldTags = oldRule?.segment?.allocation?.exclusiveTo;
  const newTags = newRule?.segment?.allocation?.exclusiveTo;

  const tags = [...(oldTags ?? []), ...(newTags ?? [])];

  if (tags.length === 0) {
    return null;
  }

  const surfaceMap = new Map<
    string,
    { oldTags: string[]; newTags: string[] }
  >();

  // Cluster by surface name. Keep track of old tags and new tags for each surface.
  oldTags?.forEach(tag => {
    if (!tagIsExclusivityGroup(tag)) {
      return;
    }

    const surfaceName = extractSurfaceName(tag);
    if (!surfaceMap.has(surfaceName)) {
      surfaceMap.set(surfaceName, { oldTags: [], newTags: [] });
    }
    surfaceMap.get(surfaceName)?.oldTags.push(tag);
  });

  newTags?.forEach(tag => {
    if (!tagIsExclusivityGroup(tag)) {
      return;
    }

    const surfaceName = extractSurfaceName(tag);
    if (!surfaceMap.has(surfaceName)) {
      surfaceMap.set(surfaceName, { oldTags: [], newTags: [] });
    }
    surfaceMap.get(surfaceName)?.newTags.push(tag);
  });

  return (
    <ActivityItem.Section label="Excluding">
      <Flex direction="row" gap="1" align="start" style={{ flexWrap: 'wrap' }}>
        {Array.from(surfaceMap.entries()).map(
          ([surfaceName, { oldTags: ot, newTags: nt }]) => (
            <ExclusivityGroups
              key={surfaceName}
              oldTags={oldTags !== undefined ? ot : undefined}
              newTags={newTags !== undefined ? nt : undefined}
            />
          ),
        )}
      </Flex>
    </ActivityItem.Section>
  );
};
